<template>
  <div class="chat-main">
    <div class="chat-container">
      <div
        class="chat-messages"
        ref="chatMessages"
        id="chat-box"
        aria-live="polite"
        aria-atomic="false"
      >
        <!-- Empty state -->
        <div v-if="messages.length === 0" class="chat-empty-state">
          <div class="empty-icon">
            <i class="las la-comments"></i>
          </div>
          <h3 class="empty-title">Ask anything about the statements</h3>
          <p class="empty-subtitle">
            I can summarise accounts, compare inflows and outflows, and surface trends across the uploaded documents.
          </p>
          <div class="suggestion-chips">
            <button
              v-for="(prompt, i) in suggestedPrompts"
              :key="i"
              class="suggestion-chip"
              @click="useSuggestedPrompt(prompt)"
            >
              <i class="las la-lightbulb"></i>
              <span>{{ prompt }}</span>
            </button>
          </div>
        </div>

        <!-- Message list -->
        <template v-else>
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="message-row"
            :class="messageRowClass(msg)"
          >
            <!-- User message -->
            <template v-if="msg.role === 'user'">
              <div class="message-bubble user">{{ msg.content }}</div>
            </template>

            <!-- Thinking indicator -->
            <template v-else-if="isProcessing(msg)">
              <div class="message-body">
                <div class="message-bubble thinking">
                  <div class="thinking-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </template>

            <!-- Assistant message -->
            <template v-else>
              <div class="message-body">
                <div class="message-bubble assistant" :ref="'msg-' + index">
                  <template v-if="smdParser && currentStreamIndex === index"></template>
                  <template v-else>
                    <span v-if="msg.status === 'streaming' && msg.displayedHtml" v-html="msg.displayedHtml"></span>
                    <span v-else v-html="renderMarkdown(msg.content)"></span>
                  </template>
                </div>
                <div v-if="!isStreaming(msg, index)" class="message-actions">
                  <button
                    class="message-action-btn"
                    :title="copiedIndex === index ? 'Copied!' : 'Copy'"
                    @click="copyMessage(msg, index)"
                  >
                    <i :class="copiedIndex === index ? 'las la-check' : 'las la-copy'"></i>
                    <span>{{ copiedIndex === index ? 'Copied' : 'Copy' }}</span>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
      <form class="chat-input" @submit.prevent="sendMessage">
        <div class="input-container">
          <textarea
            ref="messageInput"
            v-model="userInput"
            rows="1"
            placeholder="Ask a question or start a conversation..."
            class="message-input"
            @input="autoResize"
            @keydown.enter.exact.prevent="sendMessage"
          ></textarea>
          <button
            type="submit"
            class="send-button"
            :disabled="!userInput.trim()"
            title="Send message"
          >
            <i class="las la-arrow-up"></i>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'

export default {
  name: 'ChatbotChat',
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    selectedFile: {
      type: Object,
      default: null
    },
    sessionFiles: {
      type: Array,
      default: () => []
    },
    candidateId: {
      type: String,
      required: false
    },
    sessionId: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      userInput: '',
      copiedIndex: null,
      suggestedPrompts: [
        'Summarise this bank statement',
        'Compare total inflows across all accounts',
        'What is the average monthly balance?',
        'Highlight any unusual transactions'
      ],
      websocket: null,
      // Streaming animation state
      chunkQueue: {},
      isAnimating: false,
      // Streaming markdown parser state
      smdParser: null,
      currentStreamIndex: null,
      chunks: '',
      smdModule: null,
      streamFlushTimer: null,
      websocketUrl: process.env.VUE_APP_SOCKET_URL,
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    }
  },
  
  async mounted() {
    if (typeof window !== 'undefined' && !window.pdfjsLib) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      };
      document.head.appendChild(script);
    }
    
    marked.setOptions({
      breaks: true,
      gfm: true,
      headerIds: false,
      mangle: false
    });
    
    this.$nextTick(() => {
      this.scrollToBottom();
    });

    // Ensure streaming dependencies are available at runtime
    await this.ensureStreamingDepsLoaded();

    // Connect to WebSocket if candidateId and sessionId are provided
    if (this.candidateId && this.sessionId) {
      this.connectWebSocket();
    }
  },
  beforeDestroy() {
    // Clean up WebSocket connection
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    if (this.streamFlushTimer) {
      clearTimeout(this.streamFlushTimer);
      this.streamFlushTimer = null;
    }
  },
  methods: {
    scrollToBottom() {
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
      }
    },
    messageRowClass(msg) {
      if (msg.role === 'user') return 'user-row';
      return 'assistant-row';
    },
    isStreaming(msg, index) {
      return this.currentStreamIndex === index ||
        (msg && msg.status === 'streaming') ||
        this.isProcessing(msg);
    },
    autoResize() {
      const el = this.$refs.messageInput;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 140) + 'px';
    },
    useSuggestedPrompt(prompt) {
      this.userInput = prompt;
      this.sendMessage();
    },
    async copyMessage(msg, index) {
      const text = (msg && msg.content) || '';
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.copiedIndex = index;
        setTimeout(() => {
          if (this.copiedIndex === index) this.copiedIndex = null;
        }, 2000);
      } catch (e) {
        console.error('Failed to copy message:', e);
      }
    },
    async ensureStreamingDepsLoaded() {
      const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

      try {
        if (typeof window !== 'undefined' && !window.DOMPurify) {
          await loadScript('https://cdn.jsdelivr.net/npm/dompurify@3.1.6/dist/purify.min.js');
        }
      } catch (e) {}

      try {
        if (!this.smdModule) {
          this.smdModule = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/streaming-markdown/smd.min.js');
        }
      } catch (e) {}
    },
    
    isProcessing(msg) {
      if (typeof msg === 'string') {
        return msg === 'Thinking...' || (typeof msg === 'string' && msg.includes('Processing...'));
      }
      
      if (typeof msg === 'object' && msg !== null) {
        return msg.content === 'Thinking...';
      }
      
      return false;
    },
    renderMarkdown(content) {
      // Ensure content is a string
      if (!content || typeof content !== 'string') {
        return content || '';
      }
      
      try {
        // Use marked library for proper markdown parsing
        return marked(content);
      } catch (error) {
        console.error('Error parsing markdown:', error);
        // Fallback to basic HTML escaping
        return this.escapeHtml(content);
      }
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    async sendMessage() {
      if (!this.userInput.trim()) return;
      
      const messageContent = this.userInput.trim();
      
      // Send message via WebSocket if connected
      if (this.candidateId && this.sessionId) {
        this.sendWebSocketMessage({
          content: messageContent,
          candidate_id: this.candidateId,
          session_id: this.sessionId
        });
      }
      
      this.$emit('send-message', messageContent);
      this.userInput = '';

      await this.$nextTick();
      if (this.$refs.messageInput) {
        this.$refs.messageInput.style.height = 'auto';
      }
      this.scrollToBottom();
    },
    
    connectWebSocket() {
      try {
        const wsUrl = `${process.env.VUE_APP_SOCKET_URL}/chatbot/${this.candidateId}/${this.sessionId}/`;
        console.log('Connecting to WebSocket:', wsUrl);
        
        this.websocket = new WebSocket(wsUrl);
        
        this.websocket.onopen = () => {
          console.log('WebSocket connected successfully');
          this.$emit('connection-change', 'connected');
        };
        
        this.websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'stream_chunk') {
              this.handleStreamChunk(data);
            } else if (data.status === 'failed') {
              this.finalizeAssistantStream();
              this.$emit('query-error', data);
            } else if (data.status === 'completed') {
              this.finalizeAssistantStream();
              this.$emit('query-complete', data);
            } else {
              this.$emit('websocket-message', data);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        this.websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.$emit('connection-change', 'error');
        };
        
        this.websocket.onclose = () => {
          console.log('WebSocket disconnected');
          this.$emit('connection-change', 'disconnected');
        };
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
        this.$emit('connection-change', 'error');
      }
    },
    
    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }
    },
    
    isWebSocketConnected() {
      return this.websocket && this.websocket.readyState === WebSocket.OPEN;
    },

    sendWebSocketMessage(message) {
      if (this.isWebSocketConnected()) {
        message.message_type = 'chat_message';
        message.chat_type = 'financing';
        this.websocket.send(JSON.stringify(message));
        return true;
      }
      console.warn('WebSocket is not connected. Cannot send message.');
      return false;
    },
    
    // --- HANDLE INCOMING STREAMING DATA ---
    handleStreamChunk(data) {
      let lastAssistantMessage = null;
      let messageIndex = -1;

      // find the currently streaming assistant message
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const candidate = this.messages[i];
        if (candidate && candidate.role === 'assistant' &&
            (candidate.status === 'streaming' || candidate.content === 'Thinking...')) {
          lastAssistantMessage = candidate;
          messageIndex = i;
          break;
        }
      }

      if (lastAssistantMessage) {
        if (lastAssistantMessage.content === 'Thinking...') {
          lastAssistantMessage.content = '';
          this.$set(lastAssistantMessage, 'displayedContent', '');
          this.$set(lastAssistantMessage, 'displayedHtml', '');
        }

        if (!lastAssistantMessage.displayedContent) {
          this.$set(lastAssistantMessage, 'displayedContent', '');
        }

        // Initialize streaming markdown renderer on first chunk for this message
        if (this.currentStreamIndex !== messageIndex) {
          this.startAssistantStream(messageIndex);
        }

        // Append raw chunk text (for markdown conversion fallback)
        const chunk = data.content || '';
        lastAssistantMessage.content += chunk;
        lastAssistantMessage.status = 'streaming';

        if (chunk && messageIndex >= 0) {
          // Simplified secure streaming pattern
          if (this.smdParser && this.currentStreamIndex === messageIndex && this.smdModule) {
            // Accumulate chunks for security check
            this.chunks = (this.chunks || '') + chunk;
            try {
              if (window && window.DOMPurify) {
                window.DOMPurify.sanitize(this.chunks);
                if (window.DOMPurify.removed && window.DOMPurify.removed.length) {
                  if (this.smdModule.parser_end) this.smdModule.parser_end(this.smdParser);
                  return;
                }
              }

              // Parse the current chunk
              if (this.smdModule.parser_write) this.smdModule.parser_write(this.smdParser, chunk);
              this.$nextTick(() => this.scrollToBottom());
            } catch (e) {
              this.enqueueChunk(messageIndex, chunk);
            }
          } else {
            // Fallback to existing typewriter animation
            this.enqueueChunk(messageIndex, chunk);
          }
        }
      }
    },

    // Initialize streaming markdown parser for a given message index
    startAssistantStream(messageIndex) {
      this.currentStreamIndex = messageIndex;
      this.buffer = '';
      this.chunks = '';

      let container = this.$refs['msg-' + messageIndex];
      if (Array.isArray(container)) container = container[0];
      if (!container) {
        this.$nextTick(() => {
          let retry = this.$refs['msg-' + messageIndex];
          if (Array.isArray(retry)) retry = retry[0];
          if (!retry) return;
          this.startAssistantStream(messageIndex);
        });
        return;
      }

      container.innerHTML = '';

      if (!this.smdModule || !this.smdModule.default_renderer || !this.smdModule.parser) {
        return;
      }

      const renderer = this.smdModule.default_renderer(container);
      this.smdParser = this.smdModule.parser(renderer);

      try {
        const existing = (this.messages[messageIndex] && this.messages[messageIndex].content) || '';
        if (existing && this.smdModule && this.smdModule.parser_write) {
          if (window && window.DOMPurify) {
            window.DOMPurify.sanitize(existing);
            if (window.DOMPurify.removed && window.DOMPurify.removed.length) {
              if (this.smdModule.parser_end) this.smdModule.parser_end(this.smdParser);
              return;
            }
          }
          this.smdModule.parser_write(this.smdParser, existing);
        }
      } catch (e) {
        
      }
    },

    finalizeAssistantStream() {
      if (this.smdParser && this.smdModule && this.smdModule.parser_end) {
        try { this.smdModule.parser_end(this.smdParser); } catch (_) {}
      }

      const streamIndex = this.currentStreamIndex;
      if (streamIndex !== null) {
        let container = this.$refs['msg-' + streamIndex];
        if (Array.isArray(container)) container = container[0];
        if (container) container.innerHTML = '';

        const msg = this.messages[streamIndex];
        if (msg) {
          this.$delete(msg, 'displayedHtml');
          this.$delete(msg, 'displayedContent');
        }
      }

      this.smdParser = null;
      this.currentStreamIndex = null;
      this.chunks = '';
    },

    // --- CHUNK QUEUE FOR SMOOTH RENDER ---
    enqueueChunk(messageIndex, chunk) {
      if (!this.chunkQueue) this.chunkQueue = {};
      if (!this.chunkQueue[messageIndex]) this.chunkQueue[messageIndex] = [];

      this.chunkQueue[messageIndex].push(chunk);
      this.processQueue(messageIndex);
    },

    async processQueue(messageIndex) {
      if (this.isAnimating) return; // prevent overlapping
      this.isAnimating = true;

      while (this.chunkQueue[messageIndex] && this.chunkQueue[messageIndex].length > 0) {
        const chunk = this.chunkQueue[messageIndex].shift();
        await this.animateChunk(messageIndex, chunk);
      }

      this.isAnimating = false;
    },

    // --- ANIMATE CHUNK CHARACTER-BY-CHARACTER ---
    async animateChunk(messageIndex, chunk) {
      const typingSpeed = 7; 
      for (let i = 0; i < chunk.length; i++) {
        const msg = this.messages[messageIndex];
        if (!msg) break;

        msg.displayedContent = (msg.displayedContent || '') + chunk[i];

        if (i % 10 === 0 || i === chunk.length - 1) {
          const formatted = this.renderMarkdown(msg.displayedContent);
          this.$set(msg, 'displayedHtml', formatted);
          this.$nextTick(() => this.scrollToBottom());
        }

        await new Promise(r => setTimeout(r, typingSpeed));
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.chat-main {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow: hidden;
  flex: 1;
  position: relative;
}

.chat-container {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.chat-header {
  background: #ffffff;
  border-bottom: 1px solid #eaeaea;
  padding: 1rem 1.5rem;
  
  .header-title {
    @extend %title;
    font-size: 1.2rem;
    color: #333333;
  }
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 1.5rem 1rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  height: 100%;
  max-height: calc(100vh - 230px);
  min-height: 250px;
  position: relative;
  scroll-behavior: smooth;
  scrollbar-gutter: stable both-edges;
  
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    transition: background 0.2s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }
  
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
  
  // Firefox scrollbar styling
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}

// Empty state
.chat-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1rem;
  max-width: 640px;
  margin: 0 auto;

  .empty-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($color-primary, 0.15);
    margin-bottom: 1.25rem;

    i {
      font-size: 2.25rem;
      color: darken($color-primary, 25%);
    }
  }

  .empty-title {
    font-family: $brand-font-medium;
    font-size: 1.35rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 0.5rem 0;
  }

  .empty-subtitle {
    font-family: $brand-font-regular;
    font-size: 0.95rem;
    color: #718096;
    line-height: 1.5;
    margin: 0 0 1.75rem 0;
    max-width: 480px;
  }
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}

.suggestion-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.65rem 1rem;
  font-family: $brand-font-regular;
  font-size: 0.9rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  i {
    font-size: 1rem;
    color: darken($color-primary, 20%);
  }

  &:hover {
    border-color: $color-primary;
    background: rgba($color-primary, 0.08);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

// Message rows
.message-row {
  display: flex;
  gap: 0.75rem;
  width: 100%;
  max-width: 900px;
  animation: messageSlideIn 0.3s ease-out;

  &.user-row {
    justify-content: flex-end;
  }

  &.assistant-row {
    justify-content: flex-start;
    align-items: flex-start;
  }
}

.message-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  flex: 1;
}

.message-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.35rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message-row.assistant-row:hover .message-actions {
  opacity: 1;
}

.message-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  padding: 0.2rem 0.4rem;
  border-radius: 6px;
  font-family: $brand-font-regular;
  font-size: 0.78rem;
  color: #718096;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 0.9rem;
  }

  &:hover {
    background: #f1f5f9;
    color: #2d3748;
  }
}

.message-bubble {
  padding: 0.75rem 1rem;
  font-family: $brand-font-regular;
  line-height: 1.4;
  word-wrap: break-word;
  
  &.user {
    margin-left: auto;
    background: $color-primary;
    color: $color-secondary;
    border-radius: 18px 18px 4px 18px;
    max-width: 75%;
    min-width: 60px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }
  
  &.assistant {
    margin-right: auto;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 4px 14px 14px 14px;
    color: #333333;
    width: 100%;
    max-width: 100%;
    padding: 1rem 1.25rem;
    
    // Enhanced markdown styling
    ::v-deep {
      h1, h2, h3, h4, h5, h6 {
        margin: 0.85rem 0 0.4rem 0;
        color: #333333;
        font-family: $brand-font-medium;
        font-weight: 600;
        line-height: 1.3;
      }

      h1 { font-size: 1.2rem; }
      h2 { font-size: 1.1rem; }
      h3 { font-size: 1rem; }
      h4, h5, h6 { font-size: 0.95rem; }

      p {
        margin: 0.5rem 0;
        line-height: 1.5;
      }

      strong {
        font-weight: 600;
        color: #333333;
      }

      em {
        font-style: italic;
        color: #555555;
      }

      code {
        background: #f1f3f4;
        padding: 0.2rem 0.4rem;
        border-radius: 3px;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.9em;
        color: #d73a49;
      }

      pre {
        background: #f6f8fa;
        padding: 1rem;
        border-radius: 6px;
        overflow-x: auto;
        margin: 0.75rem 0;
        border: 1px solid #e1e4e8;

        code {
          background: none;
          padding: 0;
          color: #24292e;
          font-size: 0.9em;
          line-height: 1.4;
        }
      }

      blockquote {
        border-left: 4px solid $color-primary;
        margin: 0.75rem 0;
        padding: 0.5rem 0 0.5rem 1rem;
        background: #f8f9fa;
        border-radius: 0 4px 4px 0;
        color: #555555;
        font-style: italic;
      }

      ol, ul {
        margin: 0.75rem 0;
        padding-left: 1.5rem;
        color: #333333;
      }

      li {
        margin: 0.25rem 0;
        line-height: 1.4;
      }

      table {
        border-collapse: collapse;
        width: 100%;
        margin: 0.75rem 0;
        font-size: 0.9em;
      }

      th, td {
        border: 1px solid #e1e4e8;
        padding: 0.5rem;
        text-align: left;
      }

      th {
        background: #f6f8fa;
        font-weight: 600;
      }

      hr {
        border: none;
        border-top: 1px solid #e1e4e8;
        margin: 1rem 0;
      }

      a {
        color: $color-primary;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
        margin: 0.5rem 0;
      }

      // Collapse leading/trailing margins so bubbles stay tight
      > *:first-child { margin-top: 0; }
      > *:last-child { margin-bottom: 0; }
    }
    
    // Apply smooth transitions to content
    span {
      transition: opacity 0.15s ease;
    }
  }
  
  &.thinking {
    margin-right: auto;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 4px 14px 14px 14px;
    color: #333333;
    width: fit-content;
    padding: 0.85rem 1.1rem;
  }
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.thinking-dots {
  display: flex;
  gap: 0.25rem;
  
  span {
    width: 8px;
    height: 8px;
    background: $color-primary;
    border-radius: 50%;
    animation: thinking 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
    &:nth-child(3) { animation-delay: 0s; }
  }
}

@keyframes thinking {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0.4;
  }
  to {
    opacity: 1;
  }
}

// Typing cursor style
.typing-cursor {
  display: inline-block;
  color: $color-primary;
  font-weight: bold;
  margin-left: 2px;
  animation: blink 1s steps(2, start) infinite;
}

@keyframes blink {
  0%, 49% {
    opacity: 1;
  }
  50%, 100% {
    opacity: 0;
  }
}

.chat-input {
  padding: 0.5rem 1.5rem 1.25rem;
  position: relative;
  flex-shrink: 0;
  background: #ffffff;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 10;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid #dee2e6;
  border-radius: 24px;
  padding: 0.4rem 0.4rem 0.4rem 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus-within {
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba(115, 243, 169, 0.15), 0 4px 20px rgba(0, 0, 0, 0.12);
  }
}

.message-input {
  flex: 1;
  display: block;
  padding: 0.55rem 0.75rem;
  border: none;
  border-radius: 12px;
  font-family: $brand-font-regular;
  font-size: 0.95rem;
  line-height: 1.5;
  background: transparent;
  color: #495057;
  resize: none;
  max-height: 140px;
  overflow-y: auto;
  vertical-align: middle;
  
  &:focus {
    outline: none;
    color: #495057;
    background: transparent;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
  
  &:disabled {
    background: transparent;
    color: #6c757d;
    cursor: not-allowed;
  }

  // Slim scrollbar for taller inputs
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
}

.send-button {
  background: $color-primary;
  color: $color-background;
  border: none;
  padding: 0.25rem;
  border-radius: 50%;
  font-family: $brand-font-medium;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  
  &:hover:not(:disabled) {
    background: darken($color-primary, 10%);
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    background: #6c757d;
    color: #ffffff;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
  
  i {
    font-size: 0.9rem;
    color: $color-background;
  }
}
</style> 